import { Checkbox, FormControlLabel, FormGroup, Stack, TextField, Typography } from "@mui/material";
import { ActionModuleEditorState, EditorActionModule } from "../../../types/actionModule/editor/actionModuleEditorTypes";
import IconAndColorPicker from "../../../common/ui/IconAndColorPicker";
import { useTranslation } from "react-i18next";


interface Props {
    editorState: ActionModuleEditorState;
    onChanged: (value: Partial<EditorActionModule>) => void;
}

export default function CommonActionModulePropertiesEditor(props: Props) {
    const actionModule = props.editorState.actionModule;

    return <div style={{
        height: "100%",
        overflowY: "auto",
    }}>
        <ActionModulePropertyEditor
            actionModule={actionModule}
            onChanged={props.onChanged} />
    </div>;
}

interface ActionModulePropertyEditor {
    actionModule: EditorActionModule | null;
    onChanged: (value: Partial<EditorActionModule>) => void;
}

function ActionModulePropertyEditor(props: ActionModulePropertyEditor) {
    const { t } = useTranslation();
    const actionModule = props.actionModule;

    return <Stack direction="column" sx={{ p: 2 }}>
        <Typography variant="subtitle2">{t("actionModuleEditor.tabGeneral.title")}</Typography>
        <FormGroup sx={{ gap: 2, pb: 5, pt: 1 }}>
            <Stack direction="row" sx={{gap: 2}}>
                <TextField
                    value={actionModule?.name ?? ""}
                    onChange={(e) => props.onChanged({
                        name: e.target.value
                    })}
                    fullWidth
                    size="small"
                    label={t("actionModuleEditor.tabGeneral.name")} />
                {
                    actionModule != null &&
                    <IconAndColorPicker
                        parentId={actionModule.id}
                        parentType="actionModule"
                        iconData={{
                            colorHex: actionModule.colorHex,
                            iconName: actionModule.iconName,
                            iconUrl: actionModule.iconUrl
                        }}
                        uploadDisabled={!actionModule.id}
                        onChange={(updates) => props.onChanged({
                            iconName: updates.iconName,
                            colorHex: updates.colorHex,
                            iconUrl: updates.iconUrl,
                        })}
                    />
                }
            </Stack>
            <TextField
                value={actionModule?.description ?? ""}
                onChange={(e) => props.onChanged({
                    description: e.target.value
                })}
                size="small"
                label={t("actionModuleEditor.tabGeneral.description")} />
            <TextField
                value={actionModule?.employeeDescription ?? ""}
                onChange={(e) => props.onChanged({
                    employeeDescription: e.target.value
                })}
                size="small"
                label={t("actionModuleEditor.tabGeneral.employeeDescription")} />
            <TextField
                value={actionModule?.positiveAnswerText ?? ""}
                onChange={(e) => props.onChanged({
                    positiveAnswerText: e.target.value
                })}
                size="small"
                label={t("actionModuleEditor.tabGeneral.positiveAnswerText")} />
            <TextField
                value={actionModule?.negativeAnswerText ?? ""}
                onChange={(e) => props.onChanged({
                    negativeAnswerText: e.target.value
                })}
                size="small"
                label={t("actionModuleEditor.tabGeneral.negativeAnswerText")} />
            <TextField
                value={actionModule?.completionDescriptionText ?? ""}
                onChange={(e) => props.onChanged({
                    completionDescriptionText: e.target.value
                })}
                size="small"
                label={t("actionModuleEditor.tabGeneral.completionDescription")} />
        </FormGroup>

        <Typography variant="subtitle2">{t("actionModuleEditor.tabGeneral.titleCommunication")}</Typography>
        <FormGroup sx={{ pb: 5, gap: 1 }}>
            <Stack direction="row">
                <FormControlLabel
                    control={<Checkbox
                        checked={actionModule?.detailedEmailNotification ?? false}
                        onChange={(e) => props.onChanged({
                            detailedEmailNotification: e.target.checked,
                        })}
                    />}
                    label={t("actionModuleEditor.tabGeneral.detailedEmailNotification")} />
                <FormControlLabel
                    control={<Checkbox
                        checked={actionModule?.summaryEmailNotification ?? false}
                        onChange={(e) => props.onChanged({
                            summaryEmailNotification: e.target.checked,
                        })}
                    />}
                    label={t("actionModuleEditor.tabGeneral.summaryEmailNotification")} />
            </Stack>
            <TextField
                value={actionModule?.emailNotificationAddresses?.join(", ") ?? ""}
                onChange={(e) => props.onChanged({
                    emailNotificationAddresses: e.target.value
                        .split(",")
                        .map((a) => a.trim())
                        .filter((a) => a.length > 0)
                })}
                size="small"
                label={t("actionModuleEditor.tabGeneral.emailNotificationAddress")}
                helperText={t("actionModuleEditor.tabGeneral.emailNotificationAddressHelper")} />
        </FormGroup>

        <Typography variant="subtitle2">{t("actionModuleEditor.tabGeneral.titleExtendedSettings")}</Typography>
        <FormGroup>
            <TextField
                value={actionModule?.maxUserCanComplete ?? ""}
                onChange={(e) => props.onChanged({
                    maxUserCanComplete: Number(e.target.value)
                })}
                size="small"
                label={t("actionModuleEditor.tabGeneral.maxUserCanComplete")} />
        </FormGroup>
    </Stack>
}